import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDrag } from '@use-gesture/react';
import { toast } from 'sonner';
import { playGoldSound, isSoundEnabled, setSoundEnabled } from '../hooks/useGoldSound';

const API = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live') + '/api';
const SWIPE_THRESHOLD = 100;
const ROTATION_FACTOR = 0.05;

// Action overlay shown during swipe
const SwipeIndicator = ({ direction, intensity }) => {
  if (!direction || intensity < 0.3) return null;
  const config = {
    left:  { color: '#A8071A', label: 'ÉCARTER',  icon: 'M19 14l-7 7-7-7M12 21V3' }, // arrow
    right: { color: '#D4AF37', label: 'RANGER',   icon: 'M3 7l9 6 9-6V5l-9 6-9-6v2z' },
    up:    { color: '#D4AF37', label: 'AU LIVRE', icon: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z' },
    down:  { color: '#A8071A', label: 'CORBEILLE', icon: 'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' },
  };
  const c = config[direction];
  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: c.color + Math.floor(intensity * 60).toString(16).padStart(2, '0'),
      borderRadius: 16, border: `3px solid ${c.color}`,
      opacity: Math.min(intensity * 1.4, 0.95),
      transition: 'opacity 0.05s',
    }}>
      <div style={{ textAlign: 'center', color: c.color, fontFamily: 'Georgia, serif' }}>
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke={c.color} strokeWidth="2.5" strokeLinecap="round">
          <path d={c.icon} />
        </svg>
        <p style={{ fontSize: 24, fontWeight: 700, letterSpacing: 2, marginTop: 12 }}>{c.label}</p>
      </div>
    </div>
  );
};

// Sous-composant : la photo affichée dans la carte. Possède son propre état de chargement.
// Remonté automatiquement à chaque changement de photo grâce au prop `key` côté parent,
// ce qui élimine toute race-condition d'état entre prefetch et useEffect.
const CardImage = ({ photoId, token }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  return (
    <>
      <img src={`${API}/photos/${photoId}/thumbnail?token=${token}`} alt=""
        draggable="false"
        onLoad={() => setLoaded(true)}
        onError={() => { setError(true); setLoaded(true); }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none',
                 opacity: loaded && !error ? 1 : 0, transition: 'opacity 0.2s ease' }} />
      {!loaded && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'Georgia, serif', fontSize: 13, color: '#D4AF37', letterSpacing: 1, opacity: 0.7, pointerEvents: 'none' }}>
          Chargement…
        </div>
      )}
      {error && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'Georgia, serif', fontSize: 13, color: '#F7E6BD', textAlign: 'center', padding: 20, opacity: 0.85, pointerEvents: 'none' }}>
          Image indisponible.<br/>Glissez pour passer.
        </div>
      )}
    </>
  );
};

const TriExpressMode = ({ photos, albums, token, onClose, onRefresh, onAlbumPhotoAdded }) => {
  const [index, setIndex] = useState(0);
  const [drag, setDrag] = useState({ x: 0, y: 0, dir: null, intensity: 0 });
  const [exiting, setExiting] = useState(null); // direction of card exit animation
  const [pickerOpen, setPickerOpen] = useState(false);
  const [stats, setStats] = useState({ ranged: 0, livre: 0, ecartes: 0, scelles: 0 });
  const [pendingPhoto, setPendingPhoto] = useState(null); // for album picker
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [localAlbums, setLocalAlbums] = useState(albums || []);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const cardRef = useRef(null);

  // Sync local albums when prop changes (parent reloads after creation)
  // Don't overwrite local state with an empty list — preserves albums created in-flow
  useEffect(() => {
    const incoming = albums || [];
    if (incoming.length === 0) return;
    setLocalAlbums((prev) => {
      const ids = new Set(incoming.map((a) => a.id));
      const localOnly = prev.filter((p) => !ids.has(p.id));
      return [...incoming, ...localOnly];
    });
  }, [albums]);

  // Safety net: if parent didn't pass albums or they're empty, fetch directly
  useEffect(() => {
    if ((albums || []).length > 0) return;
    let cancelled = false;
    fetch(`${API}/albums/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data.albums || []);
        if (list.length > 0) setLocalAlbums((prev) => {
          const ids = new Set(list.map((a) => a.id));
          const localOnly = prev.filter((p) => !ids.has(p.id));
          return [...list, ...localOnly];
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [albums, token]);

  // Prefetch the next 3 thumbnails so swiping feels instant
  useEffect(() => {
    if (!photos || photos.length === 0) return;
    const toPrefetch = [photos[index + 1], photos[index + 2], photos[index + 3]].filter(Boolean);
    const imgs = toPrefetch.map((p) => {
      const i = new Image();
      i.src = `${API}/photos/${p.id}/thumbnail?token=${token}`;
      return i;
    });
    return () => { imgs.forEach((i) => { i.src = ''; }); };
  }, [index, photos, token]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundEnabled(next);
    if (next) playGoldSound(); // preview
  };

  const total = photos.length;
  const current = photos[index];

  const handleAction = useCallback(async (direction) => {
    if (!current || exiting) return;

    // Right swipe = album picker (besoin de la photo courante avant de la quitter)
    if (direction === 'right') {
      setPendingPhoto(current);
      setPickerOpen(true);
      setDrag({ x: 0, y: 0, dir: null, intensity: 0 });
      return;
    }

    setExiting(direction);
    const photoId = current.id;

    // Mise à jour optimiste : on lance l'API en arrière-plan, sans bloquer l'UI
    if (direction === 'left' || direction === 'down') {
      setStats((s) => ({ ...s, ecartes: s.ecartes + 1 }));
      fetch(`${API}/sas/trier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_id: photoId, action: 'supprimer' }),
      }).catch(() => toast.error('Une photo n\'a pas pu \u00eatre \u00e9cart\u00e9e'));
      toast.success('Photo \u00e9cart\u00e9e', { duration: 1200 });
    } else if (direction === 'up') {
      setStats((s) => ({ ...s, livre: s.livre + 1 }));
      fetch(`${API}/sas/trier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_id: photoId, action: 'coffre' }),
      })
        .then(() => fetch(`${API}/photos/${photoId}/book`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
        }))
        .catch(() => toast.error('Une photo n\'a pas pu \u00eatre ajout\u00e9e au Livre'));
      playGoldSound();
      toast.success('Ajout\u00e9 au Livre', { duration: 1200 });
    }

    // Avance la carte tout de suite après l'animation de sortie (300ms)
    await new Promise((r) => setTimeout(r, 300));
    setIndex((i) => i + 1);
    setExiting(null);
    setDrag({ x: 0, y: 0, dir: null, intensity: 0 });
  }, [current, exiting, token]);

  const onPickAlbum = (albumId) => {
    if (!pendingPhoto) return;
    const photoId = pendingPhoto.id;
    // Optimistic UI : on ferme le picker et on avance immédiatement
    setStats((s) => ({ ...s, ranged: s.ranged + 1 }));
    setLocalAlbums((prev) => prev.map((a) => a.id === albumId
      ? { ...a, photo_count: (a.photo_count || 0) + 1, photo_ids: [...(a.photo_ids || []), photoId] }
      : a));
    if (onAlbumPhotoAdded) onAlbumPhotoAdded(albumId, photoId);
    const album = localAlbums.find((a) => a.id === albumId);
    playGoldSound();
    toast.success(`Rang\u00e9 dans \u00ab ${album?.name || '\u00c9crin'} \u00bb`, { duration: 1200 });
    setPickerOpen(false);
    setPendingPhoto(null);
    setIndex((i) => i + 1);
    // Appels API en arrière-plan
    fetch(`${API}/sas/trier`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ photo_id: photoId, action: 'coffre' }),
    })
      .then(() => fetch(`${API}/albums/${albumId}/add-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_ids: [photoId] }),
      }))
      .catch(() => toast.error('Une photo n\'a pas pu \u00eatre rang\u00e9e'));
  };

  const onSealNoAlbum = () => {
    if (!pendingPhoto) return;
    const photoId = pendingPhoto.id;
    setStats((s) => ({ ...s, scelles: s.scelles + 1 }));
    playGoldSound();
    toast.success('Scell\u00e9e dans le Coffre-fort', { duration: 1200 });
    setPickerOpen(false);
    setPendingPhoto(null);
    setIndex((i) => i + 1);
    fetch(`${API}/sas/trier`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ photo_id: photoId, action: 'coffre' }),
    }).catch(() => toast.error('Une photo n\'a pas pu \u00eatre scell\u00e9e'));
  };

  const onCreateAlbum = async () => {
    const name = newAlbumName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API}/albums/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Création impossible');
      const newAlbum = data.album;
      setLocalAlbums((prev) => [...prev, newAlbum]);
      setNewAlbumName('');
      setCreatingAlbum(false);
      // Auto-range the pending photo into the freshly created album
      await onPickAlbum(newAlbum.id);
    } catch (e) {
      toast.error(e.message || 'Création impossible');
    }
  };

  const bind = useDrag(
    ({ down, movement: [mx, my], velocity: [vx, vy], direction: [dx, dy] }) => {
      if (exiting || pickerOpen) return;
      const absX = Math.abs(mx);
      const absY = Math.abs(my);
      let dir = null;
      let intensity = 0;
      if (absX > absY) {
        dir = mx > 0 ? 'right' : 'left';
        intensity = Math.min(absX / SWIPE_THRESHOLD, 1);
      } else {
        dir = my > 0 ? 'down' : 'up';
        intensity = Math.min(absY / SWIPE_THRESHOLD, 1);
      }
      if (down) {
        setDrag({ x: mx, y: my, dir, intensity });
      } else {
        // released
        if (intensity >= 1 || (vx > 0.4 && absX > 60) || (vy > 0.4 && absY > 60)) {
          handleAction(dir);
        } else {
          setDrag({ x: 0, y: 0, dir: null, intensity: 0 });
        }
      }
    },
    { filterTaps: true, axis: undefined }
  );

  const finished = index >= total;
  const cardTransform = exiting
    ? (() => {
        const offX = exiting === 'left' ? -1500 : exiting === 'right' ? 1500 : 0;
        const offY = exiting === 'up' ? -1500 : exiting === 'down' ? 1500 : 0;
        const rot = (offX || drag.x) * ROTATION_FACTOR;
        return `translate(${offX}px, ${offY}px) rotate(${rot}deg)`;
      })()
    : `translate(${drag.x}px, ${drag.y}px) rotate(${drag.x * ROTATION_FACTOR}deg)`;

  return (
    <div data-testid="tri-express-overlay"
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000',
               display: 'flex', flexDirection: 'column', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(212,175,55,0.15)' }}>
        <button data-testid="tri-express-close" onClick={onClose}
          style={{ fontFamily: 'Georgia, serif', fontSize: 13, padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                   background: 'transparent', color: '#F7E6BD', border: '1px solid rgba(212,175,55,0.4)' }}>
          ✕ Sortir
        </button>
        <div style={{ textAlign: 'center', fontFamily: 'Georgia, serif', color: '#F7E6BD' }}>
          <div style={{ fontSize: 18, fontWeight: 500 }}>Tri Express</div>
          <div style={{ fontSize: 12, color: '#D4AF37', marginTop: 2 }}>
            {Math.min(index + 1, total)} / {total}
          </div>
        </div>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: 11, color: '#F7E6BD', textAlign: 'right', minWidth: 90 }}>
          <div>📚 {stats.livre}</div>
          <div>🔒 {stats.scelles}</div>
          <div>📁 {stats.ranged}</div>
          <div>✕ {stats.ecartes}</div>
        </div>
      </div>

      {/* Card area */}
      <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        {finished ? (
          <div style={{ textAlign: 'center', fontFamily: 'Georgia, serif', color: '#F7E6BD' }}>
            <p style={{ fontSize: 28, color: '#D4AF37', marginBottom: 12 }}>Sas vidé</p>
            <p style={{ fontSize: 14, marginBottom: 20 }}>
              {stats.livre} ajouté{stats.livre > 1 ? 's' : ''} au Livre · {stats.scelles} scellé{stats.scelles > 1 ? 's' : ''} · {stats.ranged} rangé{stats.ranged > 1 ? 's' : ''} · {stats.ecartes} écarté{stats.ecartes > 1 ? 's' : ''}
            </p>
            <button data-testid="tri-express-finish" onClick={() => { playGoldSound(); onRefresh && onRefresh(); onClose(); }}
              style={{ fontFamily: 'Georgia, serif', fontSize: 14, padding: '12px 28px', borderRadius: 10, cursor: 'pointer',
                       background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', fontWeight: 600 }}>
              Terminer
            </button>
          </div>
        ) : current ? (
          <>
            {/* Hint legends */}
            <div style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#A8071A', fontFamily: 'Georgia, serif', fontSize: 11, opacity: 0.5, writingMode: 'vertical-rl', textOrientation: 'mixed' }}>← écarter</div>
            <div style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#D4AF37', fontFamily: 'Georgia, serif', fontSize: 11, opacity: 0.5, writingMode: 'vertical-rl', textOrientation: 'mixed' }}>ranger →</div>
            <div style={{ position: 'absolute', top: 64, left: '50%', transform: 'translateX(-50%)', color: '#D4AF37', fontFamily: 'Georgia, serif', fontSize: 11, opacity: 0.5 }}>↑ au Livre</div>

            {/* Card */}
            <div ref={cardRef} {...bind()} data-testid="tri-express-card"
              style={{
                position: 'relative',
                width: 'min(90vw, 480px)',
                height: 'min(80vh, 640px)',
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                background: '#0a0a0a',
                border: '1px solid rgba(212,175,55,0.3)',
                transform: cardTransform,
                transition: exiting ? 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none',
                cursor: 'grab',
              }}>
              <CardImage key={current.id} photoId={current.id} token={token} />
              <SwipeIndicator direction={drag.dir} intensity={drag.intensity} />
            </div>
          </>
        ) : null}
      </div>

      {/* Footer hint */}
      {!finished && (
        <div style={{ padding: '12px 20px', textAlign: 'center', fontFamily: 'Georgia, serif', fontSize: 11, color: '#F7E6BD', opacity: 0.55, borderTop: '1px solid rgba(212,175,55,0.1)' }}>
          ← Écarter · ↑ Livre · → Ranger dans un Écrin · ↓ Corbeille
        </div>
      )}

      {/* Album picker */}
      {pickerOpen && (
        <div data-testid="tri-album-picker" onClick={() => { setPickerOpen(false); setPendingPhoto(null); setCreatingAlbum(false); setNewAlbumName(''); }}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 16, padding: 24, maxWidth: 420, width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
            <p style={{ fontFamily: 'Georgia, serif', fontSize: 18, color: '#D4AF37', textAlign: 'center', marginBottom: 16 }}>Ranger cette photo</p>

            {/* Always-available: seal without album */}
            <button data-testid="tri-seal-no-album" onClick={onSealNoAlbum}
              style={{ width: '100%', fontFamily: 'Georgia, serif', fontSize: 14, padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                       background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', fontWeight: 600, marginBottom: 12 }}>
              Sceller sans Écrin
            </button>

            {/* Existing albums (exclude the photo Book if any) */}
            {(localAlbums || []).filter((a) => !a.is_book).length > 0 && (
              <>
                <p style={{ fontFamily: 'Georgia, serif', fontSize: 12, color: '#F7E6BD', opacity: 0.6, marginBottom: 8, marginTop: 4 }}>
                  ou ranger dans un Écrin existant
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(localAlbums || []).filter((a) => !a.is_book).map((a) => (
                    <button key={a.id} data-testid={`tri-pick-album-${a.id}`} onClick={() => onPickAlbum(a.id)}
                      style={{ fontFamily: 'Georgia, serif', fontSize: 14, padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                               background: 'rgba(212,175,55,0.06)', color: '#F7E6BD', border: '1px solid rgba(212,175,55,0.3)', textAlign: 'left' }}>
                      {a.name} <span style={{ fontSize: 11, color: '#D4AF37', marginLeft: 8 }}>({a.photo_count || 0})</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Create new album inline */}
            {!creatingAlbum ? (
              <button data-testid="tri-create-album" onClick={() => setCreatingAlbum(true)}
                style={{ marginTop: 12, width: '100%', fontFamily: 'Georgia, serif', fontSize: 13, padding: '11px 16px', borderRadius: 10, cursor: 'pointer',
                         background: 'transparent', color: '#D4AF37', border: '1px dashed rgba(212,175,55,0.5)', textAlign: 'center' }}>
                + Créer un nouvel Écrin
              </button>
            ) : (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input data-testid="tri-create-album-input" autoFocus value={newAlbumName}
                  onChange={(e) => setNewAlbumName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onCreateAlbum(); if (e.key === 'Escape') { setCreatingAlbum(false); setNewAlbumName(''); } }}
                  placeholder="Nom de l'Écrin"
                  style={{ fontFamily: 'Georgia, serif', fontSize: 14, padding: '11px 14px', borderRadius: 8,
                           background: '#000', color: '#F7E6BD', border: '1px solid rgba(212,175,55,0.4)', outline: 'none' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button data-testid="tri-create-album-confirm" onClick={onCreateAlbum} disabled={!newAlbumName.trim()}
                    style={{ flex: 1, fontFamily: 'Georgia, serif', fontSize: 13, padding: '10px 16px', borderRadius: 8,
                             cursor: newAlbumName.trim() ? 'pointer' : 'not-allowed',
                             background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', fontWeight: 600,
                             opacity: newAlbumName.trim() ? 1 : 0.5 }}>
                    Créer & ranger
                  </button>
                  <button onClick={() => { setCreatingAlbum(false); setNewAlbumName(''); }}
                    style={{ fontFamily: 'Georgia, serif', fontSize: 13, padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
                             background: 'transparent', color: '#F7E6BD', border: '1px solid rgba(212,175,55,0.3)' }}>
                    Annuler
                  </button>
                </div>
              </div>
            )}

            <button onClick={() => { setPickerOpen(false); setPendingPhoto(null); setCreatingAlbum(false); setNewAlbumName(''); }}
              style={{ marginTop: 16, fontFamily: 'Georgia, serif', fontSize: 13, padding: '10px 20px', borderRadius: 8, cursor: 'pointer',
                       background: 'transparent', color: '#F7E6BD', border: '1px solid rgba(212,175,55,0.2)', width: '100%' }}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TriExpressMode;
